import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { publicApi, apiError, publicFileUrl as fileUrl } from "@/lib/api";
import { btn, input, Field, Modal } from "@/components/kit";
import { toast } from "sonner";
import { Loader2, ArrowRight, Workflow, Zap } from "lucide-react";

export default function BusinessHub() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);
  const [f, setF] = useState({ name: "", email: "", company: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    publicApi.get(`/public/hub/${slug}`).then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
  }, [slug]);

  useEffect(() => {
    if (data?.organization) {
      document.title = data.organization.hub_headline || `Get started with ${data.organization.name}`;
      const canonical = data.organization.slug;
      if (canonical && canonical !== slug) {
        nav(`/hub/${canonical}`, { replace: true });
      }
    }
  }, [data, slug, nav]);

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: r } = await publicApi.post(`/public/hub/${slug}/start`, { flow_id: active.id, ...f });
      toast.success("You're all set — let's begin!");
      nav(`/onboard/${r.invite_token}`);
    } catch (er) { toast.error(apiError(er)); setBusy(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center px-6"><div className="text-center"><h1 className="text-2xl font-bold">Hub not found</h1><p className="text-muted-foreground mt-2">{err}</p></div></div>;
  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  const { organization, flows } = data;
  const themeStyle = organization.primary_color
    ? { "--primary": organization.primary_color, "--accent": organization.primary_color }
    : undefined;

  const groups = {};
  flows.forEach((fl) => { const k = (fl.category || "").trim(); (groups[k] = groups[k] || []).push(fl); });
  const present = Object.keys(groups);
  const order = organization.hub_section_order || [];
  const groupKeys = [];
  if (groups[""]) groupKeys.push("");
  order.forEach((k) => { if (k && groups[k] && !groupKeys.includes(k)) groupKeys.push(k); });
  present.filter((k) => k && !groupKeys.includes(k)).sort((a, b) => a.localeCompare(b)).forEach((k) => groupKeys.push(k));

  const Card = (fl) => (
    <div key={fl.id} className="bg-card border border-border p-6 flex flex-col" data-testid={`hub-flow-${fl.id}`}>
      <div className="flex items-center justify-between">
        <span className="grid place-items-center h-10 w-10 bg-secondary"><Workflow className="h-5 w-5" /></span>
        {fl.auto_approve && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-accent border border-accent/40 px-2 py-0.5" data-testid={`hub-instant-${fl.id}`}><Zap className="h-3 w-3" /> Instant start</span>
        )}
      </div>
      <h3 className="text-lg font-semibold mt-4">{fl.name}</h3>
      <p className="text-sm text-muted-foreground mt-1 flex-1">{fl.description || "Complete this onboarding."}</p>
      <p className="text-xs text-muted-foreground mt-3">{fl.stages} stages · {fl.tasks} steps</p>
      <button onClick={() => { setActive(fl); setF({ name: "", email: "", company: "" }); }} className={`${btn.primary} w-full mt-4`} data-testid={`hub-start-${fl.id}`}>Start now <ArrowRight className="h-4 w-4" /></button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" style={themeStyle} data-testid="hub-root">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          {organization.logo ? <img src={fileUrl(organization.logo)} alt={organization.name} className="h-8" /> : <span className="font-bold text-lg">{organization.name}</span>}
          <span className="overline text-muted-foreground">Powered by OnboardHQ</span>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-6 py-16">
        {organization.hub_image && (
          <img src={fileUrl(organization.hub_image)} alt={organization.name} className="w-full h-56 object-cover border border-border mb-10" data-testid="hub-hero-image" />
        )}
        <p className="overline text-accent mb-3">Onboarding hub</p>
        {organization.hub_headline
          ? <h1 className="text-4xl sm:text-5xl font-light tracking-tighter" data-testid="hub-headline">{organization.hub_headline}</h1>
          : <h1 className="text-4xl sm:text-5xl font-light tracking-tighter" data-testid="hub-headline">Get started with <span className="font-black">{organization.name}</span></h1>}
        <p className="mt-4 text-foreground/70 max-w-xl" data-testid="hub-intro">{organization.hub_intro || "Pick a form below to begin. You can save your progress and come back anytime."}</p>

        {flows.length === 0 ? (
          <div className="mt-12 border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="hub-empty">No public forms available yet. Please check back soon.</div>
        ) : (
          <div className="mt-12 space-y-12">
            {groupKeys.map((key) => (
              <div key={key || "_uncat"} data-testid={`hub-section-${key ? key.toLowerCase().replace(/\s+/g, "-") : "general"}`}>
                {key && <h2 className="text-lg font-semibold mb-4 pb-2 border-b border-border" data-testid="hub-section-heading">{key}</h2>}
                <div className="grid md:grid-cols-2 gap-4">
                  {groups[key].map((fl) => Card(fl))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={!!active} onClose={() => setActive(null)} title={active ? `Start: ${active.name}` : ""} testid="hub-start-modal">
        <form onSubmit={start} className="space-y-4">
          <Field label="Your name"><input required className={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="hub-name" /></Field>
          <Field label="Email"><input type="email" required className={input} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="hub-email" /></Field>
          <Field label="Company (optional)"><input className={input} value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="hub-company" /></Field>
          <button type="submit" disabled={busy} className={`${btn.primary} w-full`} data-testid="hub-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Begin onboarding <ArrowRight className="h-4 w-4" /></>}</button>
        </form>
      </Modal>
    </div>
  );
}
